import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useMatches, useNavigate } from 'react-router';
import { useSetAtom } from 'jotai';

import { warmRouteData } from '~/modules/root/util/warm-route-data';
import { markRestoreAtTop } from '~/modules/root/component/scroll-restoration/restore-at-top';
import { footerSuppressedAtom } from '~/state/ui-state-footer';

/** How small the next page's opening starts, as a share of its full size. */
export const NEXT_LISTING_START_SCALE = 0.22;

/**
 * How far above exactly-in-place the section's top may sit and still count
 * as full size. Fractional layout heights leave the scroll clamp a
 * sub-pixel short of zero on some viewports, and a turn gated on exactly
 * zero never fires there.
 */
const FULL_SIZE_SLACK_PX = 2;

/**
 * How long the page is held still waiting for the next one, at most.
 *
 * The next page is normally here within a frame or two — its data was
 * warmed as the reader approached — but a page that never arrives must not
 * leave the reader unable to scroll. Only the input hold expires: the
 * landing stays armed for whenever the page does arrive.
 */
const HANDOVER_TIMEOUT_MS = 3000;

/**
 * How far out the next page's route data is warmed, in viewports of
 * approach. Near enough that the warm's short shelf life still covers the
 * arrival, far enough that a steady read never reaches the foot cold.
 */
const WARM_AHEAD_VIEWPORTS = 2;

/**
 * How long a warm is trusted before the approach fires another. Inside the
 * max-age the server puts on a prefetch-purposed data response — 20s in
 * prod, 10s in dev (see handleDataRequest in entry.server) — so a reader
 * who lingers over the closing sections keeps a fresh copy waiting in
 * either environment. A cadence between the two leaves dev turns hitting
 * an expired entry half the time.
 */
const WARM_TTL_MS = 8_000;

/**
 * The beat of stillness at full size before the page turns.
 *
 * The section is the last thing the document can scroll to, so a fast flick
 * ends at the scroll clamp with the opening grown — and the flick's own
 * momentum spent against the clamp, not carried into the next page.
 * Committing only once the scrolling has stopped means the turn always
 * starts from a resting picture, and the landing reproduces exactly that
 * picture.
 */
const SETTLE_MS = 140;

/**
 * How many of the opening's leading pictures must be decoded before the
 * turn is offered. What the reader is looking at when the page turns has to
 * already be pixels, or the swap lands on a still-decoding frame.
 */
const DECODE_SLIDES = 4;

/** A picture that will not decode must not park the turn forever. */
const DECODE_CAP_MS = 4000;

/** The keys that scroll a page, held while the page turns. */
const SCROLL_KEYS = new Set([
    ' ',
    'ArrowDown',
    'ArrowUp',
    'PageDown',
    'PageUp',
    'Home',
    'End',
]);

/**
 * Stops the page scrolling until the returned release is called.
 *
 * Done by swallowing the input rather than by clipping the document: hiding
 * the document's overflow takes the scrollbar with it, and on a page whose
 * every rem is a share of the viewport, that is a reflow of everything.
 */
const holdScroll = () => {
    const swallow = (event: Event) => event.preventDefault();
    const swallowKey = (event: KeyboardEvent) => {
        if (SCROLL_KEYS.has(event.key)) event.preventDefault();
    };
    const options = { passive: false, capture: true } as const;
    window.addEventListener('wheel', swallow, options);
    window.addEventListener('touchmove', swallow, options);
    window.addEventListener('keydown', swallowKey, options);
    return () => {
        window.removeEventListener('wheel', swallow, options);
        window.removeEventListener('touchmove', swallow, options);
        window.removeEventListener('keydown', swallowKey, options);
    };
};

/** What is carried across the page turn. */
interface Handover {
    /** The page the turn started on, so the landing runs once, on the next. */
    from: string;
    /** The page the turn was for, so the landing never moves any other. */
    to: string;
    /** The departed entry's history key, marked at landing so Back returns
     * to that page's top rather than the clamp it was left at. Marked only
     * after the navigation, because ScrollRestoration saves the departing
     * position under whatever key the entry answers to at that moment —
     * marked any earlier, the clamp is saved under the marked key and Back
     * restores it anyway. */
    fromKey: string;
    /** Where the grown frame's top edge sat in the viewport at the turn. */
    frameTop: number;
}

export interface NextListingTurnOptions {
    /** The page the turn goes to. Undefined stands the whole turn down. */
    link: string | undefined;
    /**
     * The next page's own opening has answered: what is growing is what
     * that page paints, not a listing stand-in. The turn never fires
     * without it — a swap from a stand-in changes the picture at the very
     * moment it is supposed to hold still.
     */
    answered: boolean;
    /**
     * True while the section renders. Claims the end of the document: the
     * footer stands down so the scroll clamp lands on the grown opening.
     */
    active: boolean;
    /** Finds the arriving page's anchor, e.g. `[data-product-gallery]`. */
    anchorSelector: string;
    /** Fired once at the moment the turn commits, before the navigation. */
    onCommit: () => void;
    /**
     * Draws the growth. Called with the progress (0 at one viewport out,
     * 1 at full size) and the scale the frame has been set to; the frame's
     * transform itself is already written.
     */
    onProgress: (progress: number, scale: number) => void;
}

/**
 * The mechanics of a detail page's next-listing turn, shared by every
 * surface that ends on one: the scroll-driven growth, the route-data warm
 * on approach, the readiness gates, the settled commit, the input hold and
 * the landing. The section's look — what grows and how it fades — stays
 * with the caller, drawn through `onProgress`.
 *
 * The caller's section must be the last content in the document and
 * exactly one viewport tall (`h-screen overflow-hidden`): the scroll clamp
 * is the hold, so a flick spends its momentum against the document's end
 * with the next page's opening exactly in place, and there is no position
 * past its title to overshoot to.
 *
 * The turn commits once the reader has watched the growth, the opening has
 * answered and decoded, and the scrolling has settled at full size. The
 * landing then puts the arriving page's anchor where the grown frame was —
 * on the page the turn was for, at its top when the anchor is missing, and
 * nowhere on any other page.
 *
 * Nothing runs under reduced motion, and nothing navigates on its own for
 * a reader who has one: the opening is drawn at full size and stays a
 * link, which is the same destination without the page moving under them.
 */
export const useNextListingTurn = ({
    link,
    answered,
    active,
    anchorSelector,
    onCommit,
    onProgress,
}: NextListingTurnOptions) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = location;
    // The entry's key, read at commit time through a ref so the effect can
    // stay keyed on the pathname alone.
    const locationKey = useRef(location.key);
    locationKey.current = location.key;

    const sectionRef = useRef<HTMLElement>(null);
    const frameRef = useRef<HTMLDivElement>(null);
    const committed = useRef(false);
    const handover = useRef<Handover | null>(null);
    const releaseHold = useRef<(() => void) | null>(null);
    const holdTimeout = useRef<number | undefined>(undefined);
    // Whether the reader has watched the opening grow on this page. The
    // turn is only ever the end of that growth: a page that opens already
    // at its foot — Back from the page it turned into restores it there —
    // must not turn again on arrival, or Back never lands anywhere. A ref
    // rather than effect state: the effect re-runs when readiness flips,
    // and that must not forget growth already watched — a reader parked at
    // the scroll clamp when the last picture decodes has nothing left to
    // scroll.
    const seenGrowing = useRef(false);
    // What the turn is currently seeded for, so only a change of page
    // clears the refs above — not a readiness re-run.
    const seededFor = useRef<string | null>(null);
    // The drawing and the commit callback are per-render closures; held as
    // latest-refs so they never churn the effect below — a re-run per
    // render would rebind the listeners and re-fire the warm every frame.
    const draw = useRef(onProgress);
    draw.current = onProgress;
    const committedCallback = useRef(onCommit);
    committedCallback.current = onCommit;

    // While the section stands it is the end of the document: the footer
    // stands down so the scroll clamp lands on the grown opening, not on
    // dead ground past it.
    const setFooterSuppressed = useSetAtom(footerSuppressedAtom);
    useEffect(() => {
        if (!active) return;
        setFooterSuppressed(true);
        return () => setFooterSuppressed(false);
    }, [active, setFooterSuppressed]);

    // The turn swaps one picture for an identical one, so the pictures the
    // reader is resting on must be decoded pixels before it is offered.
    const [decoded, setDecoded] = useState(false);
    useEffect(() => {
        if (!answered) {
            setDecoded(false);
            return;
        }
        let cancelled = false;
        const frame = frameRef.current;
        const images = frame
            ? Array.from(frame.querySelectorAll('img')).slice(0, DECODE_SLIDES)
            : [];
        const settled = Promise.allSettled(
            images.map((image) => image.decode?.()),
        );
        const cap = new Promise((resolve) => {
            window.setTimeout(resolve, DECODE_CAP_MS);
        });
        Promise.race([settled, cap]).then(() => {
            if (!cancelled) setDecoded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [answered]);

    // Everything below root, in match order — RR joins _routes in that
    // order, and a detail-to-detail navigation revalidates exactly that
    // set: root never revalidates, and the layouts revalidate on pathname
    // changes.
    const matches = useMatches();
    const revalidatingRouteIds = matches
        .slice(1)
        .map((match) => match.id)
        .join(',');

    const releaseInputHold = () => {
        releaseHold.current?.();
        releaseHold.current = null;
    };

    const release = () => {
        window.clearTimeout(holdTimeout.current);
        releaseInputHold();
        handover.current = null;
    };

    // Landing: the next page is in the document. Put its anchor where the
    // grown frame was, before anything is painted, and let go of the
    // scroll.
    useLayoutEffect(() => {
        const turn = handover.current;
        if (!turn || turn.from === pathname) return;
        // The departed page was left at the scroll clamp, looking at this
        // page's opening. Back must read as back to that page — its top —
        // not restore a full-size picture of the page the reader just came
        // back from.
        markRestoreAtTop(turn.fromKey);
        // Wherever this is, it is not the page the turn was for — the
        // reader got somewhere else during the hold. Not this landing's to
        // move.
        if (turn.to !== pathname) {
            release();
            return;
        }
        const target = document.querySelector<HTMLElement>(anchorSelector);
        // A next page with no anchor still lands somewhere definite: its
        // top, not wherever the last page's scroll happened to sit.
        const top = target
            ? target.getBoundingClientRect().top +
              window.scrollY -
              turn.frameTop
            : 0;
        window.scrollTo({ top, behavior: 'instant' });
        release();
    });

    useEffect(() => {
        const section = sectionRef.current;
        const frame = frameRef.current;
        if (!section || !frame || !link) return;

        // A new page under the section: whatever the last one left behind
        // is cleared, or the second turn would never happen.
        const seeded = `${pathname}|${link}`;
        if (seededFor.current !== seeded) {
            seededFor.current = seeded;
            committed.current = false;
            seenGrowing.current = false;
            frame.style.transform = `scale(${NEXT_LISTING_START_SCALE})`;
            draw.current(0, NEXT_LISTING_START_SCALE);
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            frame.style.transform = 'scale(1)';
            draw.current(1, 1);
            return;
        }

        let ticking = false;
        let lastWarmAt = 0;
        let settleTimer: number | undefined;

        const commit = () => {
            committed.current = true;
            handover.current = {
                from: pathname,
                to: new URL(link, window.location.origin).pathname,
                fromKey: locationKey.current,
                frameTop: frame.getBoundingClientRect().top,
            };
            committedCallback.current();
            releaseHold.current = holdScroll();
            // Left where it is: the landing above puts the next page under
            // the frame, so a scroll reset here would only be undone.
            navigate(link, { preventScrollReset: true });
            // Past this only the input hold lets go, so a slow page never
            // strands the reader; the handover stays for the landing to
            // spend whenever the page does arrive.
            holdTimeout.current = window.setTimeout(
                releaseInputHold,
                HANDOVER_TIMEOUT_MS,
            );
        };

        const update = () => {
            ticking = false;
            if (committed.current) return;
            const box = section.getBoundingClientRect();
            const viewport = window.innerHeight || 1;

            // Warm the next page's data as the reader nears the foot, and
            // keep it warm while they linger: a warm has a short shelf
            // life, so one fired on arrival would be stale again by the
            // turn.
            if (
                box.top < viewport * (WARM_AHEAD_VIEWPORTS + 1) &&
                Date.now() - lastWarmAt > WARM_TTL_MS
            ) {
                lastWarmAt = Date.now();
                warmRouteData(link, revalidatingRouteIds);
            }

            // 0 as the section's top reaches the bottom of the screen, 1
            // once it reaches the top: the section is then the next page's
            // opening at the next page's own position.
            const travelled = viewport - box.top;
            const progress = Math.min(Math.max(travelled / viewport, 0), 1);
            const scale =
                NEXT_LISTING_START_SCALE +
                (1 - NEXT_LISTING_START_SCALE) * progress;
            frame.style.transform = `scale(${scale.toFixed(4)})`;
            draw.current(progress, scale);

            if (box.top > FULL_SIZE_SLACK_PX) {
                seenGrowing.current = true;
                window.clearTimeout(settleTimer);
                return;
            }
            // Only a growth of the real opening, watched to full size, ever
            // turns the page — and only once its pictures are decoded.
            // Without all three the opening stays a link.
            if (!seenGrowing.current || !answered || !decoded) {
                return;
            }
            // At full size, wait for the scrolling to stop. The section is
            // the document's end, so a flick spends its momentum against
            // the scroll clamp and the turn always starts from a resting
            // picture.
            window.clearTimeout(settleTimer);
            settleTimer = window.setTimeout(commit, SETTLE_MS);
        };

        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            window.clearTimeout(settleTimer);
        };
    }, [navigate, link, pathname, answered, decoded, revalidatingRouteIds]);

    // Whatever holds the scroll goes with the section.
    useEffect(() => release, []);

    return { sectionRef, frameRef };
};
